import { normalizeStation } from './station';

// The station segment is lowercased before it goes in a path: the API expects
// lowercase slugs, so an uppercase `?station=WPHM` embed would otherwise 404.
// Single definition shared by every builder below so the three media routes
// can't drift from buildUrl's normalization.
//
// 🔑 It is now the SAME helper the Faro stamps use (src/utils/station.js), so "the
// request URL and the RUM label agree about which station this is" is structural
// rather than two independent toLowerCase() calls that nothing compares. The
// telemetry stamps used to pass the customer's embed-code spelling through raw,
// which split 162 measured station labels across two cases apiece.
const lc = normalizeStation;

/**
 * Build the `/{station}/playlist/widget.json` request URL for a given
 * api host and a sparse parameter bag.
 *
 * Only sets URLSearchParams entries for truthy values, so falsy slots
 * (zero, empty string, null/undefined) are omitted from the query
 * string — this matches the behavior the original Vue method had.
 */
export const buildUrl = (apiPrefix, { shared, zone, station, page, perPage, category, id, limit }) => {
  const usp = new URLSearchParams();
  if (category) usp.set('category', category);
  if (page) usp.set('page', page);
  if (perPage) usp.set('perPage', perPage);
  if (shared) usp.set('include', shared);
  if (zone) usp.set('zone', zone);
  if (id) usp.set('id', id);
  if (limit) usp.set('limit', limit);

  return `${apiPrefix}/${lc(station)}/playlist/widget.json?${usp.toString()}`;
};

/**
 * Build the v4 show-metadata (`really_all.json`) URL off the api prefix.
 *
 * Previously composed from the hardcoded `SHOW_METADATA_HOST` (the prod v4
 * host) in app.vue; taking the prefix lets it honour the
 * `apiDomain` config param so a dev-cluster or uat preview doesn't silently hit
 * prod. Ported from `master` c8036c6, which the Vue 3 branch never received.
 */
export const buildShowMetadataUrl = (apiPrefix, station) =>
  `${apiPrefix}/${lc(station)}/api/v1/show/really_all.json`;

/**
 * Build a playlist RSS feed URL off the api prefix. Previously composed from
 * the hardcoded `RSS_HOST` (the prod API host) in the v4 PlayerMenu subscribe
 * action.
 */
export const buildRssUrl = (apiPrefix, station, feedId) =>
  `${apiPrefix}/${lc(station)}/playlist/rss/${feedId}.xml`;

/**
 * Build an episode download URL by joining the api prefix and the episode's
 * relative m4a path. Previously composed from the hardcoded `DOWNLOAD_HOST`
 * (the prod v4 host) in the v4 PlayerMenu download action — this mirrors
 * v4 Player.vue's `m4aURI` computed, which already joins `apiPrefix` +
 * `m4aFile`, so the download link and the thing that actually plays now agree
 * on a host instead of disagreeing by default.
 */
export const buildDownloadUrl = (apiPrefix, m4aFile) => `${apiPrefix}${m4aFile}`;
